import PropTypes from 'prop-types'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'

// third-party
import { CognitoUser, AuthenticationDetails, CognitoUserPool } from 'amazon-cognito-identity-js'

// project imports
import Loader from 'components/Loader'
import LOGIN_STEPS from 'enums/Login'
import { useGetUserQuery, useUpdateUserMutation } from 'store/api/merklebaseAPI'
import {
  isTokenValid,
  refreshToken,
  parseJWTToken,
  setSessionToken,
  isUserSessionAvailable,
} from 'utils/tokens'
import { datadogRum } from '@datadog/browser-rum'
import { mapUser } from 'utils/mappers/user'
import { organizationsTypes } from 'merklebase-utils/enums'
import { useLocation } from 'react-router-dom'
import { useGetTenantInfoBySlugQuery } from 'store/api/ssoAPI'
import useSnackbar from 'hooks/useSnackbar'
import { getSsoLoginUrl, getSsoLogoutUrl } from 'utils/sso'
import useTenant from 'hooks/useTenant'

export const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_USER_POOL_ID || '',
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
})

// ==============================|| AWS COGNITO - CONTEXT & PROVIDER ||============================== //

const AWSCognitoContext = createContext(null)

export const AWSCognitoProvider = ({ children }) => {
  const location = useLocation()
  const { openSnackbar } = useSnackbar()
  const [loginSteps, setLoginSteps] = useState(LOGIN_STEPS.USER_PASSWORD)
  const [associateMFACode, setAssociateMFACode] = useState(null)
  const [cognitoUser, setCognitoUser] = useState(null)
  const [associateVerificationCode, setAssociateVerificationCode] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [userId, setUserId] = useState(null)
  const { data: user, error: userError } = useGetUserQuery(userId, {
    skip: !userId,
  })
  const [updateUser] = useUpdateUserMutation()

  const { tenantSlug } = useTenant()

  const userTenant = useMemo(() => user?.organization_name ?? null, [user?.organization_name])

  const {
    data: tenantInfo,
    error: tenantInfoError,
    isLoading: isTenantInfoLoading,
  } = useGetTenantInfoBySlugQuery(tenantSlug)

  useEffect(() => {
    if (location.pathname === '/maintenance/404' || !window.origin.endsWith('merklebase.io')) return
    if (!tenantSlug || tenantInfoError?.originalStatus === 404) {
      window.location.href = '/maintenance/404'
    }
  }, [location.pathname, tenantInfoError, tenantSlug])

  const isCurrentlySsoSession = useMemo(() => {
    return localStorage.getItem('isSsoSession')
  }, [])

  const isUserAndPasswordLoginEnabled = useMemo(() => {
    // on non 404 error, default to user and password login
    if (!isTenantInfoLoading && tenantInfoError && tenantInfoError?.originalStatus !== 404) {
      return true
    }

    return tenantInfo?.tenant?.['username-password'] ?? true
  }, [isTenantInfoLoading, tenantInfo?.tenant, tenantInfoError])

  const isSsoEnabled = useMemo(() => {
    return tenantInfo?.tenant?.saml ?? false
  }, [tenantInfo?.tenant?.saml])

  useEffect(() => {
    if (!user?.id) return

    datadogRum.setUser({
      id: user?.id,
      name: user?.first_name + ' ' + user?.last_name,
      email: user?.email,
      organizationName: user?.organization_name,
      organizationId: user?.organization_id,
      tenantName: user?.tenantName,
    })
  }, [
    user?.id,
    user?.first_name,
    user?.last_name,
    user?.email,
    user?.organization_id,
    user?.organization_name,
    user?.tenantName,
  ])

  // if user was deactivated it will be null or there's an error fetching user data
  // so we need to log out the user
  useEffect(() => {
    if (userId && isLoggedIn && userError) {
      console.error('User data is null or error fetching user:', userError)
      logOutUser()
    }
  }, [userId, isLoggedIn, userError])

  const logInUser = useCallback(
    (accessToken) => {
      setUserId(accessToken.sub)
      setIsLoggedIn(true)
      setIsInitialized(true)
      try {
        updateUser({ id: accessToken.sub, updateLoggedAt: true })
      } catch (e) {
        console.error('Error updating user last logged at', e)
      }
    },
    [updateUser]
  )

  useEffect(() => {
    if (location.pathname !== '/sso-callback' || localStorage.getItem('serviceToken')) return

    const callback = async () => {
      const params = new URLSearchParams(location.hash.replace('#', '?'))

      const accessToken = params.get('access_token')

      if (!accessToken) {
        openSnackbar({
          message: `Failed to authorize user, please try again or contact support`,
          color: 'error',
        })
        return
      }
      setSessionToken(accessToken)
      localStorage.setItem('isSsoSession', 'true')
      const parsed = parseJWTToken(accessToken)
      logInUser(parsed)
    }

    callback()
  }, [location.hash, location.pathname, location.search, logInUser, openSnackbar])

  const logOutUser = () => {
    setSessionToken()
    setIsLoggedIn(false)
    setIsInitialized(true)
  }

  useEffect(() => {
    const init = async () => {
      try {
        const { accessToken = null } = JSON.parse(window.localStorage.getItem('serviceToken')) || {}

        // Get username from serviceToken jwt
        const localStorageCognitoUser = accessToken ? parseJWTToken(accessToken) : null

        const isAccessTokenValid = isTokenValid(accessToken)

        if (
          isAccessTokenValid &&
          ((!isCurrentlySsoSession && (await isUserSessionAvailable(accessToken))) ||
            isCurrentlySsoSession)
        ) {
          logInUser(localStorageCognitoUser)
        } else {
          if (accessToken && (await refreshToken(accessToken))) {
            logInUser(localStorageCognitoUser)
          } else {
            logOutUser()
          }
        }
      } catch (err) {
        console.error(err)
        logOutUser()
      }
    }

    init()
  }, [isCurrentlySsoSession, logInUser])

  const login = async (username, password) => {
    const usr = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    setCognitoUser(usr)

    const authData = new AuthenticationDetails({
      Username: username,
      Password: password,
    })
    const result = await new Promise((resolve, reject) => {
      usr.authenticateUser(authData, {
        onSuccess: (data) => {
          resolve(data)
        },
        onFailure: (error) => {
          reject(error)
        },
        newPasswordRequired: () => {
          resolve()
          setLoginSteps(LOGIN_STEPS.NEW_PASSWORD)
        },
        mfaRequired: () => {
          setLoginSteps(LOGIN_STEPS.MFA)
          resolve()
        },
        totpRequired: () => {
          setLoginSteps(LOGIN_STEPS.MFA)
          resolve()
        },
        mfaSetup: async () =>
          usr.associateSoftwareToken({
            onFailure: (error) => {
              reject(error)
            },
            associateSecretCode: (code) => {
              setAssociateMFACode({
                username: usr.username,
                code,
              })
              setLoginSteps(LOGIN_STEPS.SAVE_MFA)
              resolve()
            },
          }),
      })
    })

    return result
  }

  const newPassword = async (password) => {
    const result = await new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(
        password,
        {},
        {
          onSuccess: () => {
            resolve()
          },
          onFailure: (error) => reject(error),
          newPasswordRequired: () => {
            setLoginSteps(LOGIN_STEPS.NEW_PASSWORD)
            resolve()
          },
          mfaRequired: () => {
            setLoginSteps(LOGIN_STEPS.MFA)
            resolve()
          },
          totpRequired: () => {
            setLoginSteps(LOGIN_STEPS.MFA)
            resolve()
          },
          mfaSetup: () => {
            setLoginSteps(LOGIN_STEPS.SAVE_MFA)
            resolve()
          },
        }
      )
    })

    return result
  }

  const saveMFA = async (code) => {
    const result = await new Promise((resolve, reject) => {
      cognitoUser.verifySoftwareToken(code, 'My TOTP device', {
        onSuccess: (session) => {
          setSessionToken(
            session.getAccessToken().getJwtToken(),
            session.getRefreshToken().getToken()
          )
          logInUser(session.getAccessToken().payload)
          resolve()
        },
        onFailure: (error) => reject(error),
        newPasswordRequired: () => {
          setLoginSteps(LOGIN_STEPS.NEW_PASSWORD)
          resolve()
        },
        mfaRequired: () => {
          setLoginSteps(LOGIN_STEPS.MFA)
          resolve()
        },
        totpRequired: () => {
          setLoginSteps(LOGIN_STEPS.MFA)
          resolve()
        },
        mfaSetup: () => {
          setLoginSteps(LOGIN_STEPS.SAVE_MFA)
          resolve()
        },
      })
    })

    return result
  }

  const setMFA = async (code) => {
    const result = await new Promise((resolve, reject) => {
      cognitoUser.sendMFACode(
        code,
        {
          onSuccess: (session) => {
            setSessionToken(
              session.getAccessToken().getJwtToken(),
              session.getRefreshToken().getToken()
            )
            logInUser(session.getAccessToken().payload)
            resolve()
          },
          onFailure: (error) => reject(error),
          newPasswordRequired: () => {
            setLoginSteps(LOGIN_STEPS.NEW_PASSWORD)
            resolve()
          },
          mfaRequired: () => {
            setLoginSteps(LOGIN_STEPS.MFA)
            resolve()
          },
          totpRequired: () => {
            setLoginSteps(LOGIN_STEPS.MFA)
            resolve()
          },
          mfaSetup: () => {
            setLoginSteps(LOGIN_STEPS.SAVE_MFA)
            resolve()
          },
        },
        'SOFTWARE_TOKEN_MFA'
      )
    })

    return result
  }

  const ssoLogin = useCallback(() => {
    window.location.href = getSsoLoginUrl(tenantInfo?.tenant?.saml)
  }, [tenantInfo?.tenant?.saml])

  const ssoLogout = useCallback(() => {
    localStorage.setItem('isSsoSession', null)
    window.location.href = getSsoLogoutUrl()
  }, [])

  const logout = (
    { forceSsoLogout } = {
      forceSsoLogout: false,
    }
  ) => {
    setSessionToken(null)
    setLoginSteps(LOGIN_STEPS.USER_PASSWORD)
    setIsLoggedIn(false)
    const loggedInUser = userPool.getCurrentUser()
    if (loggedInUser) {
      loggedInUser.signOut()
    }

    if (forceSsoLogout || isCurrentlySsoSession) {
      ssoLogout()
    }
  }

  const sendForgotPasswordVerificationCode = async (username) => {
    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    setCognitoUser(user)

    const result = await new Promise((resolve, reject) => {
      user.forgotPassword({
        onSuccess: (d) => {
          setLoginSteps(LOGIN_STEPS.VERIFICATION_CODE)
        },
        onFailure: (error) => {
          reject(error)
        },
      })
    })

    return result
  }

  const verifyForgotPasswordCode = async (code) => {
    setAssociateVerificationCode(code)
    setLoginSteps(LOGIN_STEPS.NEW_PASSWORD)
  }

  const resetPassword = async (newPassword) => {
    const result = await new Promise((resolve, reject) => {
      if (associateVerificationCode) {
        cognitoUser.confirmPassword(associateVerificationCode, newPassword, {
          onSuccess: () => {
            setLoginSteps(LOGIN_STEPS.USER_PASSWORD)
            resolve()
          },
          onFailure: (error) => {
            reject(error)
          },
        })
      } else {
        cognitoUser.completeNewPasswordChallenge(
          newPassword,
          {},
          {
            onSuccess: () => {
              setLoginSteps(LOGIN_STEPS.USER_PASSWORD)
              resolve()
            },
            onFailure: (error) => reject(error),
            mfaRequired: () => {
              setLoginSteps(LOGIN_STEPS.MFA)
              resolve()
            },
            mfaSetup: () =>
              cognitoUser.associateSoftwareToken({
                onFailure: (error) => {
                  reject(error)
                },
                associateSecretCode: (code) => {
                  setAssociateMFACode({
                    username: cognitoUser.username,
                    code,
                  })
                  setLoginSteps(LOGIN_STEPS.SAVE_MFA)
                  resolve()
                },
              }),
          }
        )
      }
    })

    return result
  }

  const updateProfile = () => {}

  if (isInitialized !== undefined && !isInitialized) {
    return <Loader />
  }

  return (
    <AWSCognitoContext.Provider
      value={{
        user: user ? mapUser({ user }) : undefined,
        login,
        isInitialized,
        newPassword,
        logout,
        saveMFA,
        setMFA,
        associateMFACode,
        resetPassword,
        sendForgotPasswordVerificationCode,
        verifyForgotPasswordCode,
        loginSteps,
        setLoginSteps,
        isLoggedIn,
        userTenant,
        isBookingOrganization: user?.organization_type_id === organizationsTypes.booking,
        setIsLoggedIn,
        updateProfile,
        isSsoEnabled,
        isUserAndPasswordLoginEnabled,
        ssoLogin,
      }}
    >
      {children}
    </AWSCognitoContext.Provider>
  )
}

AWSCognitoProvider.propTypes = {
  children: PropTypes.node,
}

export default AWSCognitoContext
